import * as logger from '../utils/logger';
import {
  setResumeData,
  getPlaybackHeartBeatVod,
  getPlaybackHeartBeatLive,
  getPlaybackHeartBeatRecording
} from './gvp';
import { getCurrentTracks } from './audio-subtitles';
import { getInstanceSettings, getKeyValues } from './config';
import { getQuality } from '../utils/medias';
import { streamType, youboraCustomErrors } from '../constants';
import { fireError } from './youbora';
import { ERROR_TYPES } from '../constants/errors';

const log = logger.create('Resume');
const context = cast.framework.CastReceiverContext.getInstance();
const playerManager = context.getPlayerManager();
let heartBeatTimeout, heartBeatInterval;
let content = {};

/**
 * Updates resumeData when player stops. If time is below endVideoThreshold we should remove
 * resumeData, otherwise resumeData should be updated.
 * @param {number} currentTime Time played.
 */
export async function updateResume(currentTime = playerManager.getCurrentTimeSec()) {
  const { startVideoThreshold, endVideoThreshold } = getKeyValues();
  if (currentTime > startVideoThreshold) {
    const { audio, subtitle } = getCurrentTracks();
    const resume = {
      audioCode: audio,
      subtitleCode: subtitle,
      time: content.duration - currentTime <= endVideoThreshold ? -1 : Math.floor(currentTime)
    };
    try {
      resume.time === -1 ? log.info('Resume data removed') : log.info('Resume data sent');
      await setResumeData(resume, content.id, content.itemType);
    } catch (e) {
      log.error('Error on resume', e);
    }
  }
}

/**
 * Method to start the interval of the Heartbeat.
 * @param {Object} type The data of the content being played.
 * @param {Function} handleError Function to handle errors.
 */
export async function init(type, handleError) {
  const interval = getInstanceSettings().heartBeatInterval * 1000;
  if (process.env.HEARTBEAT_DISABLED) {
    log.warn('Heartbeat DISABLED');
    return;
  }

  if (interval > 0) {
    // Heartbeat should be executed on init.
    setHeartBeatInterval(type, handleError);
    heartBeatInterval = setInterval(() => setHeartBeatInterval(type, handleError), interval);
  }
}

/**
 * Method to launch the Heartbeat depending on the content.
 * @param {Object} type The data of the content being played.
 * @param {Function} handleError Function to handle errors.
 */
function setHeartBeatInterval(type, handleError) {
  let error = null;
  if (type) {
    if (type === streamType.live) {
      error = setLiveHeartBeat();
    } else if (type === streamType.vod) {
      error = setVodHeartBeat();
    } else {
      error = setRecordingHeartBeat();
    }
  } else {
    stopHeartBeat();
  }
  error && handleError(error);
}

/**
 * Method to set the heartbeat of a VoD content.
 * @returns {String|null} Error type.
 */
async function setVodHeartBeat() {
  const heartBeatData = {
    mediaType: 'movie',
    playbackTime: Math.floor(playerManager.getCurrentTimeSec()),
    playbackHeartBeat: getInstanceSettings().heartBeatFrequency,
    movieId: content.id,
    mediaId: content.mediaId
  };
  try {
    const response = await getPlaybackHeartBeatVod(heartBeatData);
    return checkHeartBeatResponse(response);
  } catch (e) {
    return manageHeartBeatFail(e);
  }
}

/**
 * Method to set the heartbeat of a live content.
 * @returns {String|null} Error type.
 */
async function setLiveHeartBeat() {
  const heartBeatData = {
    mediaType: 'live',
    playbackHeartBeat: getInstanceSettings().heartBeatFrequency,
    channelCallLetter: content.callLetter,
    channelId: content.id,
    liveStreamId: content.mediaId,
    liveStreamQualityName: getQuality(content.quality)
  };
  try {
    const response = await getPlaybackHeartBeatLive(heartBeatData);
    return checkHeartBeatResponse(response);
  } catch (e) {
    return manageHeartBeatFail(e);
  }
}

/**
 * Method to set the heartbeat of a recorded content.
 * @returns {String|null} Error type.
 */
async function setRecordingHeartBeat() {
  const { audio, subtitle } = getCurrentTracks();
  const heartBeatData = {
    playbackTime: Math.floor(playerManager.getCurrentTimeSec()),
    playbackHeartBeat: getInstanceSettings().heartBeatFrequency,
    audioCode: audio,
    subtitleCode: subtitle,
    recordingId: content.id
  };
  try {
    const response = await getPlaybackHeartBeatRecording(heartBeatData);
    return checkHeartBeatResponse(response);
  } catch (e) {
    return manageHeartBeatFail(e);
  }
}

/**
 * Checks heartbeat response.
 * @param {Object} heartbeat Heartbeat response.
 * @returns {String|null} Error type.
 */
function checkHeartBeatResponse(heartbeat) {
  if (heartbeat.data.Content === false) {
    log.info('Several devices playing the same content/wrong response', heartbeat);
    stopHeartBeat();
    fireError({
      errorCode: youboraCustomErrors.heartbeat.code,
      msg: youboraCustomErrors.heartbeat.description
    });
    playerManager.stop();
    return ERROR_TYPES.heartbeatMultipleDevices;
  } else {
    log.info('Saved heartbeat');
    return null;
  }
}

/**
 * Manages heartbeat fails.
 * @param {Object} error Error heartbeat check.
 * @returns {String|null} Error type.
 */
function manageHeartBeatFail(error) {
  log.error('Error while setting heartbeat', error);
  if (error?.code === 'ErrorNoPlatform') {
    stopHeartBeat();
    playerManager.stop();
    return ERROR_TYPES.gvpGeneric;
  } else {
    log.info('Heartbeat request error ignored');
    return null;
  }
}

/**
 * Method to clear the heartbeat interval.
 */
export function stopHeartBeat() {
  if (!process.env.HEARTBEAT_DISABLED && (heartBeatTimeout || heartBeatInterval)) {
    log.info('Stopped heartbeat');
    clearTimeout(heartBeatTimeout);
    clearInterval(heartBeatInterval);
  }
}

/**
 * Stores content currently being played.
 */
export function setContentResume(currentContent) {
  content = currentContent;
}
